import { css } from '@emotion/react'
import {
  Text,
  SpacingToken,
  RadiusToken,
} from '@livechat/design-system-react-components'

import mediaQueries from '@/layout/media-queries'

import type { IApplication } from '@/apis/apps/interfaces'
import BillingCyclePicker from './BillingCyclePicker'
import PriceSummary from './PriceSummary'
import { DeveloperDetails } from './DeveloperDetails'

export const wrapperCss = css`
  width: 100%;
  display: grid;
  grid-template-columns: 24px 1fr 1fr;
  grid-template-rows: auto auto;
  grid-column-gap: var(${SpacingToken.Spacing1});
  grid-template-areas:
    'image details price'
    '. picker picker';

  ${mediaQueries.xsmallScreen(60)} {
    grid-template-columns: 48px 1fr auto;
    grid-template-rows: 48px auto;
    grid-template-areas:
      'image details price'
      '. picker price';
    grid-column-gap: var(${SpacingToken.Spacing4});
  }
`

const iconCss = css`
  grid-area: image;
  width: 24px;
  height: 24px;
  border-radius: var(${RadiusToken[1]});
  object-fit: contain;

  ${mediaQueries.xsmallScreen(60)} {
    width: 48px;
    height: 48px;
  }
`

const detailsCss = css`
  grid-area: details;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  overflow: hidden;

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: calc(100%);
  }

  ${mediaQueries.xsmallScreen(60)} {
    align-items: flex-start;
    justify-content: center;
  }
`

const priceCss = css`
  grid-area: price;

  > div {
    align-items: flex-end;
    white-space: nowrap;
  }
`

const pickerCss = css`
  grid-area: picker;
  display: flex;
  align-items: center;
  gap: var(${SpacingToken.Spacing2});

  > p {
    margin-top: var(${SpacingToken.Spacing2});
  }

  > div {
    max-width: 160px;
    margin-top: var(${SpacingToken.Spacing2});
  }
`

interface Props {
  item: IApplication
  hidePicker?: boolean
}

const PurchaseItem = ({ item, hidePicker }: Props) => {
  if (!item) {
    return null
  }

  return (
    <div css={wrapperCss} data-test="purchase-item-details">
      <img css={iconCss} src={item.icons?.small} alt={`${item.name} logo`} />

      <div css={detailsCss}>
        <Text size="md" bold data-test="product-details-app-name">
          {item.name}
        </Text>

        {item.ownerId && <DeveloperDetails developerId={item.ownerId} />}
      </div>

      <div css={priceCss}>
        <PriceSummary item={item} />
      </div>

      {!hidePicker && (
        <div css={pickerCss} data-test="ocp-billing-cycle-picker">
          <BillingCyclePicker />
        </div>
      )}
    </div>
  )
}

export default PurchaseItem
